import { createTheme } from "@dash/default-theme";
import { Theme, ThemeProvider } from "@emotion/react";
import { NewRouterBreadcrumbsBar } from "@legacy-megarax/ui-components";
import { TradeRouteDetailsContainer, TradeRoutesHomeContainer } from "@megarax/crm-customer-map";
import {
  AddCustomerPage,
  CustomerPage,
  CustomersViewPage,
  LegacyMegaraxPricingRulesSyncDiffPage,
  PricingRulesSyncDiffPage,
} from "@megarax/crm-customers";
import { MyAffiliateCodePage, MyAffiliateCodesPage } from "@megarax/crm-my-affiliates-codes";
import { PriceReportFormContainer } from "@megarax/crm-my-price-reports";
import { OrderDraftPage, OrderDraftsPage, OrderPage, OrdersPage } from "@megarax/crm-orders";
import { PostSaleDiscountsPage } from "@megarax/crm-post-sale-discounts";
import { PriceListDetailsContainer, PriceListHomeContainer } from "@megarax/crm-price-lists";
import { SubjectsViewContainer } from "@megarax/crm-price-reports";
import { ProductAvailabilityHome } from "@megarax/crm-product-availability";
import { ProfitTargetsPage } from "@megarax/crm-profit-targets";
import { RegionPage, RegionRedirect, RegionsHome, SalespersonRedirect } from "@megarax/crm-regions";
import {
  BcItemSyncDiffPage,
  LegacyMegaraxPriceSyncDiffPage,
  PriceListsPage,
  SheetsItemSyncDiffPage,
  SheetsPriceListSyncDiffPage,
  SheetsPriceSyncDiffPage,
  ShopifyPriceSyncDiffPage,
  ShopifyProductSyncDiffPage,
  ShopifyVariantSyncDiffPage,
  TradeItemsPage,
} from "@megarax/crm-trade-items";
import { TrainingHome } from "@megarax/crm-training";
import { UserHomepage } from "@megarax/crm-user";
import {
  DayViewContainer,
  MyVisitsRedirect,
  VisitRedirect,
  VisitsRedirect,
  WeekViewContainer,
} from "@megarax/crm-visits";
import { useConfig } from "@megarax/dev-config";
import { MegaraxWebappContainer } from "@megarax/features";
import { HomeView, MegaraxProviders } from "@megarax/root";
import { MdGroups, MdOutlineInventory2 } from "react-icons/md";
import { Navigate, Outlet, Route } from "react-router-dom-v5-compat";

import { getMegaraxPath } from "@megaron/megarax-uri";

import { SupportSettingsPage } from "./support/pages/SupportSettingsPage";
import { SupportTicketPage } from "./support/pages/SupportTicketPage";
import { SupportCreateTicketPage } from "./support/pages/SupportCreateTicketPage";
import { SupportTicketsPage } from "./support/pages/SupportTicketsPage";

const crmTheme: Theme = createTheme("273");
const catalogTheme: Theme = createTheme("150");

export const useCrmRouter = () => {
  return (
    <>
      <Route path="/catalog" element={<CatalogRouteContainer />}>
        <Route path="" element={<Navigate to="items" replace />} />
        <Route path="items" element={<TradeItemsPage />} />
        <Route path="price-lists" element={<PriceListsPage />} />
        <Route path="diff-sync/bcItem" element={<BcItemSyncDiffPage />} />
        <Route path="diff-sync/sheetsItem" element={<SheetsItemSyncDiffPage />} />
        <Route path="diff-sync/sheetsPriceList" element={<SheetsPriceListSyncDiffPage />} />
        <Route path="diff-sync/sheetsPrice" element={<SheetsPriceSyncDiffPage />} />
        <Route path="diff-sync/shopifyProduct" element={<ShopifyProductSyncDiffPage />} />
        <Route path="diff-sync/shopifyVariant" element={<ShopifyVariantSyncDiffPage />} />
        <Route path="diff-sync/shopifyPrice" element={<ShopifyPriceSyncDiffPage />} />
        <Route path="diff-sync/legacyMegaraxPrice" element={<LegacyMegaraxPriceSyncDiffPage />} />
      </Route>

      <Route path="/crm" element={<CrmRouteContainer />}>
        <Route path="diff-sync/sheetsPricingRule" element={<PricingRulesSyncDiffPage />} />
        <Route path="diff-sync/legacyMegaraxPricingRule" element={<LegacyMegaraxPricingRulesSyncDiffPage />} />
        <Route path="customers/*">
          <Route path="*" element={<Navigate to="/crm/customers/new-list" replace />} />
          <Route path=":viewId/*" element={<CustomersViewPage />} />
          <Route path="new/*" element={<AddCustomerPage />} />
          <Route path="id/:customerId/*" element={<CustomerPage />} />
        </Route>
        <Route path="regions/*">
          <Route path="*" element={<RegionsHome />} />
          <Route path="region/:region/*" element={<RegionPage />} />
        </Route>
        <Route path="product-availability/*">
          <Route path="*" element={<ProductAvailabilityHome />} />
        </Route>
        <Route path="training/*">
          <Route path="*" element={<TrainingHome />} />
        </Route>
        <Route path="post-sale-discounts" element={<PostSaleDiscountsPage />} />
        <Route path="profit-targets" element={<ProfitTargetsPage />} />
        <Route path="region-profit-metrics">
          <Route path="" element={<Navigate to={getMegaraxPath.uri("megarax://crm/regions")} replace />} />
          <Route path=":region" element={<RegionRedirect />} />
        </Route>
        <Route path="zgloszenia">
          <Route
            path=""
            element={
              <SupportTicketsPage
                basePath="/crm/zgloszenia"
                settingsPath="/crm/zgloszenia/settings"
                createPath="/crm/zgloszenia/nowe"
              />
            }
          />
          <Route path="nowe" element={<SupportCreateTicketPage listPath="/crm/zgloszenia" />} />
          <Route path="settings" element={<SupportSettingsPage />} />
          <Route path=":id" element={<SupportTicketPage listPath="/crm/zgloszenia" />} />
        </Route>
        <Route path="user-home" element={<UserHomepage />} />
        <Route path="orders">
          <Route path="" element={<OrdersPage />} />
          <Route path=":number" element={<OrderPage />} />
        </Route>
        <Route path="order-drafts">
          <Route path="" element={<OrderDraftsPage />} />
          <Route path="new/*" element={<OrderDraftPage isNew />} />
          <Route path=":orderId/*" element={<OrderDraftPage />} />
        </Route>
        <Route path="my-affiliate-codes">
          <Route path="" element={<MyAffiliateCodesPage />} />
          <Route path=":affiliateCodeId/*" element={<MyAffiliateCodePage />} />
        </Route>

        {/* Crm (POS) */}
        <Route path="pos" element={<PosCrmMegaraxWebappContainer />}>
          <Route path="" element={<HomeView />} />
          <Route path="cenniki">
            <Route path="" element={<PriceListHomeContainer />}></Route>
            <Route path="nowy" element={<PriceListHomeContainer />}></Route>
            <Route path=":uuid/:tab/*" element={<PriceListDetailsContainer />}></Route>
          </Route>
          <Route path="moje_ankiety_cenowe" element={<PriceReportFormContainer />} />
          <Route path="moje_wizyty" element={<MyVisitsRedirect />} />
          <Route path="handlowcy">
            <Route path={`:userId`}>
              <Route path="" element={<SalespersonRedirect />} />
              <Route path="wizyty">
                <Route path="" element={<VisitsRedirect />} />
                <Route path="tydzien">
                  <Route path="" element={<VisitsRedirect />} />
                  <Route path=":date/wizyta/:uuid" element={<VisitRedirect />} />
                  <Route path=":date/*" element={<WeekViewContainer />} />
                </Route>
                <Route path="dzien">
                  <Route path="" element={<VisitsRedirect />} />
                  <Route path=":date/wizyta/:uuid" element={<VisitRedirect />} />
                  <Route path=":date/*" element={<DayViewContainer />} />
                </Route>
              </Route>
            </Route>
            <Route path="" element={<Navigate to={getMegaraxPath.uri("megarax://crm/regions")} replace />} />
          </Route>
          <Route path="trasy">
            <Route path="" element={<TradeRoutesHomeContainer />} />
            <Route path=":tradeRouteUuid" element={<TradeRouteDetailsContainer />}></Route>
          </Route>
          <Route path="ankiety_cenowe">
            <Route path="" element={<Navigate to={getMegaraxPath.uri("megarax://crm/price-report-products")} />} />
            <Route path="produkty" element={<SubjectsViewContainer />} />
          </Route>
        </Route>
      </Route>
    </>
  );
};

const CrmRouteContainer = () => (
  <ThemeProvider theme={crmTheme}>
    <CrmMegaraxWebappContainer />
  </ThemeProvider>
);

const CrmMegaraxWebappContainer: React.FC = () => {
  const { config } = useConfig();

  return (
    <MegaraxWebappContainer
      logo={<MdGroups width="1em" height="1em" />}
      title="CRM"
      subtitle="megarax.net"
      bypassAuth={config.testUser.enabled}
    >
      <MegaraxProviders>
        <Outlet />
      </MegaraxProviders>
    </MegaraxWebappContainer>
  );
};

const CatalogRouteContainer = () => (
  <ThemeProvider theme={catalogTheme}>
    <CatalogMegaraxWebappContainer />
  </ThemeProvider>
);

const CatalogMegaraxWebappContainer: React.FC = () => {
  const { config } = useConfig();

  return (
    <MegaraxWebappContainer
      logo={<MdOutlineInventory2 width="1em" height="1em" />}
      title="Katalog"
      subtitle="megarax.net"
      bypassAuth={config.testUser.enabled}
    >
      <MegaraxProviders>
        <Outlet />
      </MegaraxProviders>
    </MegaraxWebappContainer>
  );
};

const PosCrmMegaraxWebappContainer = () => {
  return (
    <MegaraxProviders>
      <NewRouterBreadcrumbsBar />
      <Outlet />
    </MegaraxProviders>
  );
};
